import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PS } from '../theme';
import { Wordmark } from '../lib/ui';
import { Icon } from '../lib/icons';
import { verifyPackage } from '../lib/verifyPackage';

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const m = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
  const t = new Date(d.getTime() + 7 * 3600 * 1000);
  return `${t.getUTCDate()} ${m[t.getUTCMonth()]} ${(t.getUTCFullYear() + 543) % 100} · ${String(t.getUTCHours()).padStart(2, '0')}:${String(t.getUTCMinutes()).padStart(2, '0')} น.`;
}

const STEPS = [
  { n: '1', t: 'ขอซองหลักฐาน', d: 'สมาชิกหรือท้าวแชร์กดขอ “ซองหลักฐานปิดผนึก” จากวงของตน แล้วได้ไฟล์ .zip' },
  { n: '2', t: 'ลากไฟล์มาวางที่นี่', d: 'อัปโหลดไฟล์ .zip — การตรวจสอบทั้งหมดเกิดในเบราว์เซอร์ของคุณ ไม่ส่งขึ้นเซิร์ฟเวอร์' },
  { n: '3', t: 'อ่านผลได้ทันที', d: 'ระบบตรวจลายเซ็นดิจิทัล + สายโซ่หลักฐาน แล้วบอกว่าซองเป็นของแท้หรือถูกแก้ไข' },
];

export default function VerifyPackage() {
  const nav = useNavigate();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState('');

  const handle = useCallback(async (file) => {
    if (!file) return;
    setBusy(true); setError(null); setResult(null); setFileName(file.name);
    try {
      const r = await verifyPackage(file);
      setResult(r);
    } catch (e) {
      setError(e.message || 'ตรวจสอบไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  }, []);

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handle(f);
  };

  const reset = () => { setResult(null); setError(null); setFileName(''); if (inputRef.current) inputRef.current.value = ''; };

  return (
    <div style={{ minHeight: '100vh', background: PS.page, fontFamily: PS.fontUI, color: PS.text }}>
      {/* top bar */}
      <div style={{ position: 'sticky', top: 0, zIndex: 10, background: 'rgba(245,241,234,0.85)', backdropFilter: 'blur(12px)', borderBottom: `1px solid ${PS.lineSoft}` }}>
        <div style={{ maxWidth: 880, margin: '0 auto', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="ps-press" onClick={() => nav('/')} data-testid="verify-home-link" style={{ cursor: 'pointer' }}><Wordmark size={20} /></div>
          <button data-testid="verify-login-link" onClick={() => nav('/app')} className="ps-press" style={{ background: 'transparent', border: 'none', color: PS.textMuted, fontSize: 13, fontFamily: PS.fontUI, cursor: 'pointer' }}>เข้าสู่ระบบ</button>
        </div>
      </div>

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '32px 20px 64px' }}>
        {/* hero */}
        <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center', padding: '5px 11px', borderRadius: 999, background: PS.ink, color: PS.page, fontSize: 11.5, fontWeight: 600, marginBottom: 16 }}>
          <Icon.shield size={12} stroke="oklch(0.72 0.12 82)" /> เครื่องมือสาธารณะ · ฟรี
        </div>
        <h1 style={{ fontSize: 'clamp(28px,5vw,40px)', fontWeight: 700, letterSpacing: -1, margin: '0 0 12px', lineHeight: 1.1 }}>
          ตรวจสอบซองหลักฐาน
        </h1>
        <p style={{ fontSize: 'clamp(14px,2vw,16px)', color: PS.textMuted, lineHeight: 1.65, maxWidth: 560, margin: 0 }}>
          ทนาย คู่กรณี หรือใครก็ตามสามารถตรวจสอบ “ซองหลักฐานปิดผนึก” ของ Peershare ได้ด้วยตัวเอง
          ระบบจะตรวจ<b style={{ color: PS.text }}>ลายเซ็นดิจิทัล</b>และ<b style={{ color: PS.text }}>สายโซ่หลักฐาน</b>ว่าถูกแก้ไขหรือไม่
          — <b style={{ color: PS.forest }}>ทุกอย่างเกิดในเบราว์เซอร์ของคุณ ไฟล์ไม่ถูกอัปโหลดไปไหน</b>
        </p>

        {/* dropzone / result */}
        {!result && (
          <div
            data-testid="verify-dropzone"
            onClick={() => !busy && inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={onDrop}
            style={{
              marginTop: 28, border: `2px dashed ${drag ? PS.terracotta : PS.line}`, borderRadius: 20,
              background: drag ? 'oklch(0.62 0.12 38 / 0.05)' : PS.surface2, padding: '44px 24px',
              textAlign: 'center', cursor: busy ? 'default' : 'pointer', transition: 'border-color 0.2s ease, background 0.2s ease',
            }}>
            <input ref={inputRef} type="file" accept=".zip,application/zip" data-testid="verify-file-input"
              onChange={(e) => handle(e.target.files?.[0])} style={{ display: 'none' }} />
            <div style={{ width: 56, height: 56, borderRadius: 18, background: PS.panel, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Icon.doc size={26} stroke={PS.ink} />
            </div>
            {busy ? (
              <div className="ps-live-dot" data-testid="verify-busy" style={{ fontSize: 14, color: PS.textMuted }}>กำลังตรวจสอบ “{fileName}” …</div>
            ) : (
              <>
                <div style={{ fontSize: 15.5, fontWeight: 600 }}>ลากไฟล์ <span style={{ fontFamily: PS.fontMono }}>.zip</span> มาวางที่นี่</div>
                <div style={{ fontSize: 12.5, color: PS.textMuted, marginTop: 6 }}>หรือคลิกเพื่อเลือกไฟล์ซองหลักฐาน</div>
              </>
            )}
          </div>
        )}

        {error && (
          <div data-testid="verify-error" style={{ marginTop: 20, background: 'oklch(0.62 0.16 25 / 0.08)', border: '1px solid oklch(0.62 0.16 25 / 0.3)', borderRadius: 16, padding: '16px 18px', display: 'flex', gap: 12 }}>
            <Icon.info size={20} stroke={PS.danger} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: PS.danger }}>ตรวจสอบไม่สำเร็จ</div>
              <div style={{ fontSize: 12.5, color: PS.textMuted, marginTop: 3, lineHeight: 1.5 }}>{error}</div>
              <button data-testid="verify-retry" onClick={reset} className="ps-press" style={{ marginTop: 10, background: PS.ink, color: PS.page, border: 'none', borderRadius: 10, padding: '8px 16px', fontSize: 12.5, fontWeight: 600, fontFamily: PS.fontUI }}>ลองไฟล์อื่น</button>
            </div>
          </div>
        )}

        {result && <Results result={result} onReset={reset} />}

        {/* how it works */}
        {!result && !busy && (
          <div style={{ marginTop: 44 }}>
            <div style={{ fontSize: 11, color: PS.textMuted, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 14 }}>ทำงานอย่างไร</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
              {STEPS.map((s) => (
                <div key={s.n} style={{ background: PS.surface2, border: `1px solid ${PS.line}`, borderRadius: 16, padding: '18px' }}>
                  <div style={{ width: 30, height: 30, borderRadius: 9, background: PS.ink, color: PS.page, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, fontFamily: PS.fontNum, marginBottom: 12 }}>{s.n}</div>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{s.t}</div>
                  <div style={{ fontSize: 12.5, color: PS.textMuted, lineHeight: 1.55 }}>{s.d}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 16, background: 'oklch(0.48 0.08 155 / 0.07)', border: '1px solid oklch(0.48 0.08 155 / 0.2)', borderRadius: 14, padding: '14px 16px', fontSize: 12.5, color: PS.text, lineHeight: 1.6, display: 'flex', gap: 10 }}>
              <Icon.lock size={18} stroke={PS.forest} />
              <span>Peershare เป็นเพียง<b>คนกลางผู้เก็บหลักฐาน</b> — ไม่ตัดสินข้อพิพาท ไม่ถือเงิน ซองหลักฐานนี้ออกแบบให้ตรวจสอบได้อย่างเป็นอิสระ แม้ไม่มี Peershare ก็พิสูจน์ได้</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Results({ result, onReset }) {
  const { verified, checks, meta, members } = result;
  return (
    <div className="ps-fade" style={{ marginTop: 28 }}>
      {/* verdict */}
      <div data-testid="verify-verdict" style={{
        borderRadius: 20, padding: 'clamp(22px,4vw,32px)', color: PS.page,
        background: verified ? 'linear-gradient(135deg, oklch(0.42 0.09 155), oklch(0.34 0.07 155))' : 'linear-gradient(135deg, oklch(0.5 0.15 25), oklch(0.4 0.13 25))',
      }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {verified ? <Icon.check size={26} stroke="#fff" sw={2.5} /> : <Icon.x size={26} stroke="#fff" sw={2.5} />}
          </div>
          <div>
            <div data-testid="verify-verdict-text" style={{ fontSize: 'clamp(18px,3vw,22px)', fontWeight: 700, letterSpacing: -0.4 }}>
              {verified ? 'ซองหลักฐานนี้เป็นของแท้' : 'พบความผิดปกติ — ซองอาจถูกแก้ไข'}
            </div>
            <div style={{ fontSize: 13, opacity: 0.82, marginTop: 3 }}>
              {verified ? 'ลายเซ็นและสายโซ่หลักฐานครบถ้วน ตรวจสอบผ่านทุกขั้นตอน' : 'มีอย่างน้อยหนึ่งขั้นตอนที่ไม่ผ่าน — ใช้ความระมัดระวัง'}
            </div>
          </div>
        </div>
      </div>

      {/* checklist */}
      <div style={{ marginTop: 16, background: PS.surface2, border: `1px solid ${PS.line}`, borderRadius: 18, overflow: 'hidden' }}>
        {checks.map((c, i) => (
          <div key={c.key} data-testid={`verify-check-${c.key}`} style={{ padding: '14px 18px', borderTop: i ? `1px solid ${PS.lineSoft}` : 'none' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ width: 22, height: 22, borderRadius: 7, flexShrink: 0, background: c.ok ? 'oklch(0.48 0.08 155 / 0.15)' : 'oklch(0.62 0.16 25 / 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
                {c.ok ? <Icon.check size={14} stroke={PS.forest} sw={2.5} /> : <Icon.x size={14} stroke={PS.danger} sw={2.5} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 500 }}>{c.label}</div>
                {c.detail && <div style={{ fontSize: 11.5, color: PS.textDim, marginTop: 2, fontFamily: PS.fontMono }}>{c.detail}</div>}
                {c.sub && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {c.sub.map((s) => (
                      <span key={s.name} style={{ fontSize: 10.5, fontFamily: PS.fontMono, padding: '2px 8px', borderRadius: 6, background: s.ok ? 'oklch(0.48 0.08 155 / 0.1)' : 'oklch(0.62 0.16 25 / 0.12)', color: s.ok ? PS.forest : PS.danger }}>
                        {s.ok ? '✓' : '✕'} {s.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* package details */}
      <div style={{ marginTop: 16, background: PS.surface2, border: `1px solid ${PS.line}`, borderRadius: 18, padding: '18px' }}>
        <div style={{ fontSize: 11, color: PS.textMuted, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>รายละเอียดซอง</div>
        {[
          ['วงแชร์', result.contract?.name || meta.circle_id],
          ['ผู้ขอซอง', meta.requested_by],
          ['คู่กรณี', meta.subject],
          ['เหตุผล', meta.reason],
          ['ออกเมื่อ', fmtDate(meta.issued_at)],
          ['จำนวนสมาชิก', members?.length ? `${members.length} คน` : '—'],
          ['จำนวนเหตุการณ์', `${result.events.length} รายการ`],
        ].map(([k, v]) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '7px 0', borderBottom: `1px solid ${PS.lineSoft}` }}>
            <span style={{ fontSize: 12.5, color: PS.textMuted, flexShrink: 0 }}>{k}</span>
            <span style={{ fontSize: 12.5, fontWeight: 500, textAlign: 'right' }}>{v}</span>
          </div>
        ))}
        <div style={{ marginTop: 12, fontSize: 10.5, color: PS.textDim, fontFamily: PS.fontMono, wordBreak: 'break-all', lineHeight: 1.5 }}>
          CA public key: {meta.ca_public_key}
        </div>
      </div>

      {/* events timeline */}
      {result.events.length > 0 && (
        <div style={{ marginTop: 16, background: PS.surface2, border: `1px solid ${PS.line}`, borderRadius: 18, padding: '18px' }}>
          <div style={{ fontSize: 11, color: PS.textMuted, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 14 }}>สายโซ่หลักฐาน</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {result.events.map((e, i) => (
              <div key={e.event_id || i} style={{ display: 'flex', gap: 12, paddingBottom: i < result.events.length - 1 ? 16 : 0 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: PS.ink, flexShrink: 0, marginTop: 4 }} />
                  {i < result.events.length - 1 && <div style={{ width: 2, flex: 1, background: PS.line, marginTop: 2 }} />}
                </div>
                <div style={{ flex: 1, minWidth: 0, paddingBottom: 2 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{e.title || e.event_type}</div>
                  {e.meta && <div style={{ fontSize: 11.5, color: PS.textMuted, marginTop: 1 }}>{e.meta}</div>}
                  <div style={{ fontSize: 10, color: PS.textDim, marginTop: 3, fontFamily: PS.fontMono }}>#{e.seq} · 0x{(e.content_hash || '').slice(0, 12)}…</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <button data-testid="verify-reset-btn" onClick={onReset} className="ps-press" style={{ marginTop: 20, width: '100%', height: 50, background: PS.ink, color: PS.page, border: 'none', borderRadius: 16, fontSize: 14.5, fontWeight: 600, fontFamily: PS.fontUI }}>
        ตรวจซองอื่น
      </button>
    </div>
  );
}
